import type {
  productConfiguratorTabsEnum,
  Unit,
  wizardTypesEnum,
} from "variables/enums";
import type { Accessory, AccessoryGroup } from "interfaces/schemas/accessories";
import type { NodeKinds } from "interfaces/enums";
import type React from "react";

export interface OrderCodeChange {
  partKey: string;
  optionKey: string;
  signatureKey: string;
}

interface OrderCodeOptionState {
  partKey: string;
  optionKey: string;
  signatureKey: string;
  signatureValue: string;
  signaturePresentation: string;
  separator: string;
  isSizePart: boolean;
}

export interface PropertyState {
  id: string;
  value: string;
}

interface Signature {
  description: string;
  value: string;
  range: string;
  isDefault: boolean;
  state: number;
  key: string;
  title: string;
  presentation: string;
}

interface OrderCodeOption {
  signatures: Signature[];
  currentSignature: Signature;
  state: number;
  range: string | null;
  type: number;
  separator: string;
  key: string;
  title: string;
  presentation: string;
}

interface OrderCodePart {
  options: OrderCodeOption[];
  state: number;
  key: string;
  title: string;
  presentation: string;
}

export interface OrderCode {
  parts: OrderCodePart[];
  key: string;
  title: string;
  presentation: string;
}

export interface OrderCodeState {
  lastChange: OrderCodeChange | null;
  optionStates: OrderCodeOptionState[];
}

export interface RequestOrderCodeState {
  orderCodeState?: OrderCodeState;
  propertyStates?: PropertyState[];
  currentStrategyId?: string;
  selectedAccessories?: Accessory[];
}
export interface AccessoryInfo {
  accessoryId: string;
  accessoryGroupId: string;
  parent: {
    seriesId: number;
    state: RequestOrderCodeState;
  };
}

export interface RequestParams {
  seriesId: number;
  languageCode: string;
  resultUnitsSystem: Unit;
  accessoryInfo?: AccessoryInfo;
  state?: RequestOrderCodeState;
  countryCode: string;
}

export interface WizardRequestParams extends RequestParams {
  dummyType?: string;
  wizardType?: wizardTypesEnum;
  groupPosition?: number;
}

export interface ProductRequestData {
  id: string;
  kind: NodeKinds;
  orderCode: {
    seriesId: number;
    state: {
      optionStates: OrderCodeOptionState[];
    };
    currentStrategyId?: string;
    propertyStates?: PropertyState[];
  };
  productName: string;
  presentation: string;
  pimId?: string;
  languageCode: string;
  selectedAccessories: unknown[];
}

export type TopologyData = {
  isDummy: boolean;
  dummyType: string;
  wizardType: string;
  groupPosition: number;
};

export interface LoadDefaultState {
  language: string;
  units: Unit;
  country: string;
  seriesId: number;
  topologyData?: TopologyData;
}
export interface LoadPersistedState {
  nodeId: string;
  language: string;
  units: Unit;
  country: string;
  seriesId: number;
  topologyData?: TopologyData;
  productName: string;
}

export interface ChangeProductPayload {
  newStrategyId?: string | null;
  changeData?: {
    data?: string | null;
    partKey?: string;
    optionKey?: string;
  };
  changeInput?: {
    inputValue?: string | null;
    inputId?: string | null;
  };
  newSelectedAccessories?: Accessory[];
  isAccessoryAffected?: boolean;
  setIsProductChanged?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ChangeProductToSuggestedPayload {
  suggestedProductOrderCode: OrderCodeState;
  selectedAccessories?: Accessory[];
}

export interface ProductConfiguratorStoreActions {
  loadDefaultState: (payload: LoadDefaultState) => void;
  loadPersistedState: (payload: LoadPersistedState) => void;
  clear: () => void;
  changeProduct: (payload: ChangeProductPayload) => void;
  getProductData: (isProductChanged?: boolean) => void;
  resetData: () => void;
  changeUnits: (newUnits: Unit) => void;
  getProductRequestData: () => ProductRequestData | null;
  changeActiveTab: (newTab: productConfiguratorTabsEnum) => void;
  onSaveConfiguration: () => void;
  changeProductToSuggested: (payload: ChangeProductToSuggestedPayload) => void;
  setIsProductChanged: (value: boolean) => void;
}

export interface IterableOption {
  optionKey: string;
}

export interface IterablePart {
  partKey: string;
  options: IterableOption[];
}

export interface TargetProperty {
  id: string;
  name: string;
  valueMin: number | null;
  valueMax: number | null;
  defaultValue: number;
  ignorable: boolean;
  unit: string;
}

type GroupControlType = "Radio" | "Checkbox" | "Select";

export interface Control {
  id: string;
  title: string;
  boolValue: boolean;
  enabledBy: string | null;
  enabled: boolean;
  keyPresentation?: string;
}

export interface Group {
  title: string;
  groupControlType: GroupControlType;
  controls?: Control[];
}

export interface Section {
  title: string;
  groups: Group[];
}

interface ColumnTitles {
  key: string;
  subTitles: ColumnTitles;
  title: string;
}

export interface ListViewConfiguration {
  iterableParts: IterablePart[];
  targetProperties: TargetProperty[];
  columnTitles: ColumnTitles[];
  attachments?: Section[];
  accessories?: Section[];
  targetPropertiesTitle?: string;
}

interface ProductImage {
  pimId: string;
  text: string;
  isReportImage: boolean;
  typ: "ProductPhoto" | string;
}

interface Strategy {
  id: string;
  presentation: string;
}

export interface Product {
  id: string;
  orderCode: OrderCode;
  accessoryGroups: AccessoryGroup;
  availableStrategies: Strategy[];
  currentStrategyId: "string";
  listViewConfiguration: ListViewConfiguration;
  selectedAccessories: Accessory[];
}

export interface ProductData {
  product: Product;
  productImageList: { productImages: ProductImage[] };
  specificationText: { text: string };
}

export interface ProductStore {
  initialRequestParams: RequestParams | null;
  requestParams: RequestParams | null;
  productData: ProductData | null;
  isLoading: boolean;
  productId: string | null;
  isModalView: boolean;
  isOrderCodeLoading: boolean;
  productName: string | null;
  requestUnitsSystem: Unit;
  resultUnitsSystem: Unit;
  nodeId?: string;
  lang: string;
  error?: unknown;
  activeTab: productConfiguratorTabsEnum;
  activePart: string | null;
  activePartModal: string | null;
  isConfigurationModalOpened: boolean;
  isProductChanged: boolean;
}

export type ProductConfiguratorStore = ProductStore & {
  actions: ProductConfiguratorStoreActions;
};

export enum ValidationResult {
  None = "None",
  Information = "Information",
  Warning = "Warning",
  Critical = "Critical",
}

export interface ProductListProperty {
  propertyId: string;
  validationMessage?: string;
  validationResult?: ValidationResult;
  value: string;
}

export interface ProductListViewItem {
  ordercodeState: OrderCodeState;
  properties: ProductListProperty[];
  columnTitles: ColumnTitles[];
  selectedAccessories?: Accessory[];
}
